/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";
import React from "react";

import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";

export const RadioGroupItem = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & PropsWithClassName
>(({ className, ...props }, ref) => {
    return (
        <span className="flex h-5 items-center">
            <RadioGroupPrimitive.Item
                ref={ref}
                className={cn(
                    "my-0 aspect-square h-4 w-4 rounded-full",
                    "focus-visible:ring-ring border-2 ring-offset-surface-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    "border-content-primary bg-inherit p-0 text-content-primary disabled:cursor-not-allowed disabled:opacity-50",
                    className,
                )}
                {...props}
            >
                <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                    <Circle className="h-1.5 w-1.5 fill-current text-current" />
                </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
        </span>
    );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export type RadioListItem = {
    radio: React.ReactElement;
    label: React.ReactNode;
    hint?: React.ReactNode;
};

export const RadioGroup = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & PropsWithClassName
>(({ className, ...props }, ref) => {
    return <RadioGroupPrimitive.Root className={cn("grid gap-2", className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
